import { ref, computed, getCurrentScope, onScopeDispose, type ComputedRef, type Ref } from "vue";

export type ConnectionType = "wifi" | "cellular" | "ethernet" | "unknown";

/** Single-source `NetworkInformation` ships via the ambient
 *  augmentation at `@speedtest/network-information.d.ts`
 *  (src/features/speedtest/engine/network-information.d.ts). AC.W5d.T2
 *  retired the prior local `NavigatorConnection` duplicate so the
 *  project speaks one vocabulary about `navigator.connection`. */
function navigatorConnection(): NetworkInformation | undefined {
    return navigator.connection;
}

/**
 * Detect connection type via the Network Information API (Chrome/Edge/Android).
 * Returns null on browsers that don't support it (Safari, Firefox).
 */
export function useConnectionType() {
    const connectionType: Ref<ConnectionType | null> = ref(null);

    const isCellular: ComputedRef<boolean> = computed(
        () => connectionType.value === "cellular",
    );

    function detect(): void {
        const conn = navigatorConnection();
        if (!conn) {
            connectionType.value = null;
            return;
        }

        // `conn.type` is the most direct signal (mobile browsers only)
        if (conn.type === "cellular") {
            connectionType.value = "cellular";
            return;
        }
        if (conn.type === "wifi") {
            connectionType.value = "wifi";
            return;
        }
        if (conn.type === "ethernet") {
            connectionType.value = "ethernet";
            return;
        }

        // Desktop Chrome exposes effectiveType but not type.
        // Slow effective types strongly suggest cellular.
        if (conn.effectiveType === "2g" || conn.effectiveType === "slow-2g" || conn.effectiveType === "3g") {
            connectionType.value = "cellular";
            return;
        }

        connectionType.value = null;
    }

    detect();

    // Re-detect on network changes
    const conn = navigatorConnection();
    if (conn) {
        conn.addEventListener("change", detect);
        if (getCurrentScope()) {
            onScopeDispose(() => {
                conn.removeEventListener("change", detect);
            });
        }
    }

    return {
        connectionType,
        isCellular,
        detect,
    };
}
